import _ from 'lodash'
import { createSelector } from 'reselect'

import { paramSelector } from '../../utils/selectors'
import { RootState } from '../types'

import { ConfigLoadingState, UmbracoImage } from './types'

export const selectConfig = createSelector(
  (state: RootState) => state.config,
  c => c
)

export const getSearchFilters = createSelector(selectConfig, c => c.content?.searchFilters || [])
export const getFooterText = createSelector(selectConfig, c => c.content?.footerText || '')
export const getFooterLinks = createSelector(selectConfig, c => c.content?.footerLinks || [])
export const getNavigationLinks = createSelector(selectConfig, c => c.content?.navigationLinks || [])
export const getExternalSearchLink = createSelector(selectConfig, c => c.content?.externalSearchLink)
export const getCmsHomeUrl = createSelector(selectConfig, c => c.content?.cmsHomeUrl)
export const getCmsFaqUrl = createSelector(selectConfig, c => c.content?.cmsFaqUrl)
export const getCmsLoginUrl = createSelector(selectConfig, c => c.content?.cmsLoginUrl)
export const getCmsContactsUrl = createSelector(selectConfig, c => c.content?.cmsContactsUrl)

export const getCountryFilterEnabled = createSelector(selectConfig, c => {
  return c.content?.countryFilterEnabled || false
})
export const getDefaultCountryFilter = createSelector(selectConfig, c => c.content?.defaultCountryFilter)
export const getOtherCountriesFilter = createSelector(selectConfig, c => c.content?.otherCountriesFilter)

export const getCountryCode = createSelector(selectConfig, c => c.countryCode)

export const getSearchPageSize = createSelector(selectConfig, c => c.pageSizes['searchResult'] || 5)

export const getLocaleMap = createSelector(selectConfig, paramSelector<string>(), (c, lang) => c.localeMap[lang])

export const getRegistrationNumbers = createSelector(selectConfig, c => c.registrationNumbers || [])

export const getPublicUrl = createSelector(selectConfig, c => c.publicUrl)

export const getAccountReportsPageSize = createSelector(selectConfig, c => c.pageSizes['archiveReports'] || 10)

export const getAccountImportsPageSize = createSelector(selectConfig, c => c.pageSizes['importHistory'] || 10)

export const getCompletedReportStatus = createSelector(selectConfig, c => c.completedReportStatus)

export const getLoadedLanguage = createSelector(selectConfig, c => c.loadedLanguage)

export const canLoadUmbracoConfig = createSelector(
  selectConfig,
  paramSelector<string>(),
  getLoadedLanguage,
  (c, requestedLanguage, loadedLanguage) =>
    requestedLanguage !== loadedLanguage ||
    _.includes([ConfigLoadingState.NotLoaded, ConfigLoadingState.Failed], c.status)
)

export const getUmbracoConfigLoadStatus = createSelector(selectConfig, c => c.status)

export const getLogoUrl = createSelector(selectConfig, (c): UmbracoImage => c.content?.logo ?? { url: 'logoPrimary' })
export const getMobileLogoUrl = createSelector(
  selectConfig,
  (c): UmbracoImage => c.content?.mobileLogo ?? { url: 'logoPrimary' }
)
export const getLogoInvertedUrl = createSelector(
  selectConfig,
  (c): UmbracoImage =>
    c.content?.logoInverted ??
    c.content?.mobileLogoInverted ?? {
      url: 'logoPrimaryInverse'
    }
)
export const getMobileLogoInvertedUrl = createSelector(
  selectConfig,
  (c): UmbracoImage =>
    c.content?.mobileLogoInverted ?? {
      url: 'logoPrimaryInverse'
    }
)

export const getSessionTimeout = createSelector(selectConfig, c => c.content?.sessionTimeout)
export const getGoogleRecaptchaSiteKey = createSelector(selectConfig, c => c.googleRecaptchaSiteKey)
export const getViewHtmlIcon = createSelector(selectConfig, c => c.viewHtmlIcon)

export const getHeaderBanner = createSelector(selectConfig, c => c.content?.headerBanner)
export const getSearchWidgetDisplayMode = createSelector(selectConfig, c => c.content?.searchWidgetDisplayMode)

export const getGtmId = createSelector(selectConfig, c => c.gtmId)

export const isApiBlocked = createSelector(selectConfig, c => c.apiBlocked)

export const getPricesMode = createSelector(selectConfig, c => c.pricesMode)

export const getPrimaryIdentifier = createSelector(selectConfig, c => c.primaryIdentifier)

export const getDomesticOfferingCountries = createSelector(selectConfig, c => c.domesticOfferingCountries)

export const getDomesticOfferingFallbackCountry = createSelector(selectConfig, c => c.domesticOfferingFallbackCountry)

export const getOfficialDocumentsCountries = createSelector(selectConfig, c => c.officialDocumentsCountries)

export const getRedirectToCartOnItemAdded = createSelector(selectConfig, c => c.redirectToCartOnItemAdded)

export const getOrderProcessingTimeout = createSelector(selectConfig, c => c.orderProcessingTimeoutSec)

export const getOfficeEnabled = createSelector(selectConfig, c => c.officeEnabled)

export const getImportItemsPageSize = createSelector(selectConfig, c => c.pageSizes['importItems'] || 10)

export const getImportConfig = createSelector(selectConfig, paramSelector<string>(), (c, lang) => c.import[lang])

export const getMaximumNumberOfSupportedItems = createSelector(selectConfig, c => c.maximumNumberOfSupportedItems)

export const getShowBulkImportMenuInHeader = createSelector(selectConfig, c => c.content?.showBulkImportMenuInHeader)

export const getShowBulkImportLinkAboveSearchButton = createSelector(
  selectConfig,
  c => c.content?.showBulkImportLinkAboveSearchButton
)

export const getShowAsBulkImportButton = createSelector(selectConfig, c => c.content?.showAsBulkImportButton)

export const getInvoicesPageSize = createSelector(selectConfig, c => c.pageSizes['invoices'] || 10)

export const getContactWidget = createSelector(selectConfig, c => c.content?.contactWidget)

export const getShowMulticolumnDropdown = createSelector(selectConfig, c => c.content?.showMulticolumnDropdown)

export const getContactUsFormWidget = createSelector(selectConfig, c => c.content?.showContactUsFormWidget)

export const getContactUsReason = createSelector(selectConfig, c => c.content?.contactUsReason)

export const getPreferredProductsEnabled = createSelector(selectConfig, c => c.content?.preferredProductsEnabled)

export const isPreferredProductAllowedCountry = createSelector(
  selectConfig,
  paramSelector<string | undefined>(),
  (c, countryCode) => _.size(c.localCountries) === 0 || (countryCode && _.includes(c.localCountries, countryCode))
)

export const getLocalCountries = createSelector(selectConfig, c => c.localCountries)

export const getShowAiChatbotButton = createSelector(selectConfig, c => c.content?.showAiChatbotButton ?? false)

export const getSapAnonymousUserId = createSelector(selectConfig, c => c.content?.sapAnonymousUserId)

export const getShowAIColumsOnArchive = createSelector(selectConfig, c => c.content?.showAIColumsOnArchive ?? false)
